import { TableComponent } from '@atoms';
import {
  Col,
  DatePicker,
  Empty,
  message,
  Row,
  Select,
  Tag,
  Typography,
} from 'antd';
import { Dayjs } from 'dayjs';
import { memo, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const { Text } = Typography;

import {
  useBannerActivate,
  useBannerDeactivate,
  useBannerDelete,
  useBannerList,
} from '../hooks/useBanner';
import {
  BannerApiItem,
  BannerSegment,
  BannerSlot,
  BannerStatusEnum,
} from '../types/bannerManagement';
import { formatDate } from '../utils/utils';
import { BannerStatusTag } from './BannerStatusTag';
import { BannerTableActions } from './BannerTableActions';

const PAGE_SIZE = 15;
const { Option } = Select;

function parsePage(pageParam: string | null): number {
  if (!pageParam) return 1;

  const parsed = Number(pageParam);
  if (Number.isNaN(parsed) || parsed < 1) return 1;

  return parsed;
}

function parseFilter<T extends string>(
  value: string | null,
  options: readonly T[]
): T | undefined {
  if (!value) return undefined;

  return options.includes(value as T) ? (value as T) : undefined;
}

function getSegmentTag(segment: BannerSegment) {
  if (segment === BannerSegment.Freemium) {
    return {
      label: 'Freemium',
      style: {
        padding: '0 8px',
        color: '#EB2F96',
        border: '1px solid #891D5B',
        background: '#2A1322',
      },
    };
  }

  return {
    label: 'Assinante',
    style: {
      padding: '0 8px',
      color: '#FAAD14',
      border: '1px solid #614700',
      background: '#2B2111',
    },
  };
}

function getSlotTag(slot: BannerSlot) {
  const labelBySlot: Record<BannerSlot, string> = {
    [BannerSlot.Top]: 'Topo',
    [BannerSlot.Middle]: 'Meio',
    [BannerSlot.Bottom]: 'Fundo',
  };

  return {
    label: labelBySlot[slot],
    style: {
      padding: '0 8px',
      color: '#1677FF',
      border: '1px solid #003A8C',
      background: '#111D2C',
    },
  };
}

export const ListBannerManagementTable = memo(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState<[string, string] | undefined>(
    undefined
  );

  const segmentFilter = parseFilter(searchParams.get('segment'), [
    BannerSegment.Freemium,
    BannerSegment.Subscriber,
  ] as const);
  const slotFilter = parseFilter(searchParams.get('slot'), [
    BannerSlot.Top,
    BannerSlot.Middle,
    BannerSlot.Bottom,
  ] as const);
  const statusFilter = parseFilter(searchParams.get('status'), [
    BannerStatusEnum.Active,
    BannerStatusEnum.Inactive,
    BannerStatusEnum.Draft,
  ] as const);
  const currentPage = parsePage(searchParams.get('page'));

  const { banners, total, isLoading } = useBannerList({
    page: currentPage,
    limit: PAGE_SIZE,
    status: statusFilter,
    slotType: slotFilter,
    targetMarket: segmentFilter,
    ...(dateRange?.[0] && { startDate: dateRange[0] }),
    ...(dateRange?.[1] && { endDate: dateRange[1] }),
  });

  const { activateBanner } = useBannerActivate();
  const { deactivateBanner } = useBannerDeactivate();
  const { deleteBanner } = useBannerDelete();

  const updateQueryParams = (
    patch: Record<string, string | number | undefined>,
    resetPage = false
  ) => {
    setSearchParams(prevParams => {
      const nextParams = new URLSearchParams(prevParams);

      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === '') {
          nextParams.delete(key);
        } else {
          nextParams.set(key, String(value));
        }
      });

      if (resetPage) {
        nextParams.delete('page');
      }

      return nextParams;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      updateQueryParams({ page: safeCurrentPage }, false);
    }
  }, [currentPage, safeCurrentPage]);

  const paginatedBanners = useMemo(() => banners, [banners]);

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD'),
      ]);
    } else {
      setDateRange(undefined);
    }
    updateQueryParams({ page: 1 }, true);
  };

  const handleActivate = async (bannerId: number) => {
    try {
      await activateBanner(bannerId);
    } catch {
      message.error('Nao foi possivel publicar o banner.');
    }
  };

  const handleDeactivate = async (bannerId: number) => {
    try {
      await deactivateBanner(bannerId);
    } catch {
      message.error('Nao foi possivel desativar o banner.');
    }
  };

  const handleDelete = async (bannerId: number) => {
    try {
      await deleteBanner(bannerId);
    } catch {
      message.error('Nao foi possivel excluir o banner.');
    }
  };

  const tableColumns = useMemo(() => {
    return [
      {
        title: 'Titulo',
        dataIndex: 'title',
        render: (title: string) =>
          title ? (
            title
          ) : (
            <Text type='secondary' italic style={{ fontSize: 12 }}>
              Sem título
            </Text>
          ),
      },
      {
        title: 'Segmento',
        dataIndex: 'targetMarket',
        width: 140,
        render: (targetMarket: BannerSegment) => {
          const segmentTag = getSegmentTag(targetMarket);

          return <Tag style={segmentTag.style}>{segmentTag.label}</Tag>;
        },
      },
      {
        title: 'Slot',
        dataIndex: 'slotType',
        width: 120,
        render: (slotType: BannerSlot) => {
          const slotTag = getSlotTag(slotType);
          return <Tag style={slotTag.style}>{slotTag.label}</Tag>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 120,
        render: (status: BannerStatusEnum) => (
          <BannerStatusTag status={status} />
        ),
      },
      {
        title: 'Data de Publicacao',
        dataIndex: 'effectiveDate',
        width: 170,
        render: (date: string) => formatDate(date),
      },
      {
        title: 'Dispositivos',
        dataIndex: 'deviceType',
        width: 170,
      },
      {
        title: 'Acoes',
        dataIndex: 'actions',
        width: 70,
        align: 'center' as const,
        render: (_: unknown, record: BannerApiItem) => (
          <BannerTableActions
            bannerId={record.id}
            status={record.status}
            onPublish={handleActivate}
            onDeactivate={handleDeactivate}
            onCancelSchedule={handleDeactivate}
            onDelete={handleDelete}
          />
        ),
      },
    ];
  }, [handleActivate, handleDeactivate, handleDelete]);

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col>
          <Select
            value={segmentFilter}
            onChange={value => updateQueryParams({ segment: value }, true)}
            placeholder='Todos os Segmentos'
            style={{ width: 180 }}
            allowClear
          >
            <Option value={BannerSegment.Freemium}>Freemium</Option>
            <Option value={BannerSegment.Subscriber}>Assinante</Option>
          </Select>
        </Col>

        <Col>
          <Select
            value={slotFilter}
            onChange={value => updateQueryParams({ slot: value }, true)}
            placeholder='Todos os Slots'
            style={{ width: 180 }}
            allowClear
          >
            <Option value={BannerSlot.Top}>Topo</Option>
            <Option value={BannerSlot.Middle}>Meio</Option>
            <Option value={BannerSlot.Bottom}>Fundo</Option>
          </Select>
        </Col>

        <Col>
          <Select
            value={statusFilter}
            onChange={value => updateQueryParams({ status: value }, true)}
            placeholder='Todos os Status'
            style={{ width: 180 }}
            allowClear
          >
            <Option value={BannerStatusEnum.Active}>Ativo</Option>
            <Option value={BannerStatusEnum.Inactive}>Inativo</Option>
            <Option value={BannerStatusEnum.Draft}>Rascunho</Option>
          </Select>
        </Col>

        <Col>
          <DatePicker.RangePicker
            format='DD/MM/YYYY'
            placeholder={['Data de início', 'Data final']}
            onChange={handleDateRangeChange}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      {!isLoading && total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='Nenhum banner encontrado. Ajuste os filtros ou crie um novo banner.'
        />
      ) : (
        <TableComponent
          pageSize={PAGE_SIZE}
          columns={tableColumns}
          data={paginatedBanners}
          total={total}
          loading={isLoading}
          currentPage={safeCurrentPage}
          onChangePage={selectedPage =>
            updateQueryParams({ page: selectedPage })
          }
          hidePaginationOnSinglePage={false}
          showSizeChanger={false}
          size='default'
        />
      )}
    </>
  );
});
